/**
 * Command lifecycle helpers — the small, non-dispatcher methods that round
 * out the command-buffer slice:
 *
 *   - setCommandAxis     (live axis toggle; also queues a pending axis)
 *   - appendCommandDigit (numeric input)
 *   - backspaceCommand
 *   - cancelCommand      (Esc / right-click: restore + pop undo)
 *   - adjustLastOperation (tweak params of the last op; dispatched via
 *                          the replay-strategy registry)
 *   - clearLastOperation
 *
 * Each is a thin pure function in this module; index.ts wires them to the
 * slice factory.
 */

import { recordCommandCancel } from "../../debug/listenerProbe.ts";
import { destroyIKSession } from "../../mannequin/ikStabilizer.ts";
import { destroyWorkerSession } from "../../mannequin/ikWorkerClient.ts";
import { buildCurveEditVertices } from "../../modeling/curveOps.ts";
import { setLastSnapResult } from "../../modeling/snap.ts";
import { clearLiveEditPreview } from "../liveEditPreview.ts";
import type { AppState, AxisConstraint } from "../storeTypes.ts";
import {
  applyEditObjectSnapshot,
  applySnapshot,
  captureSnapshot,
  isEditObjectSnapshot,
  undoManager,
} from "../storeUndo.ts";
import { resolveKeyboardAxis } from "./commandAxis.ts";
import { type Get_, releaseLiveVertsScratch, type Set_ } from "./core.ts";
import { REPLAY_STRATEGIES } from "./replayStrategies.ts";

export function setCommandAxis(
  set: Set_,
  get: Get_,
  axis: AxisConstraint,
): void {
  const state = get();
  const cb = state.commandBuffer;
  if (cb) {
    // Resolve axis through transform orientation (converts to object-local
    // normalVector when needed, matching gizmo behavior)
    const resolved = resolveKeyboardAxis(state, axis);
    set({
      commandBuffer: {
        ...cb,
        axis: resolved.axis,
        normalVector: resolved.normalVector ?? null,
        mouseOrigin: null,
      },
      pendingAxis: null,
    });
  } else {
    // No active command — queue for the next command that starts
    set({ pendingAxis: axis });
  }
}

export function appendCommandDigit(set: Set_, get: Get_, char: string): void {
  const cb = get().commandBuffer;
  if (!cb) return;
  const input = cb.numericInput;
  // Minus only allowed at start
  if (char === "-") {
    if (input.length > 0) {
      // Toggle sign
      set({
        commandBuffer: {
          ...cb,
          numericInput: input.startsWith("-") ? input.slice(1) : "-" + input,
        },
      });
    } else {
      set({ commandBuffer: { ...cb, numericInput: "-" } });
    }
    return;
  }
  // Only one decimal point
  if (char === "." && input.includes(".")) return;
  set({ commandBuffer: { ...cb, numericInput: input + char } });
}

export function backspaceCommand(set: Set_, get: Get_): void {
  const cb = get().commandBuffer;
  if (!cb) return;
  set({ commandBuffer: { ...cb, numericInput: cb.numericInput.slice(0, -1) } });
}

export function cancelCommand(set: Set_, get: Get_): void {
  const state = get();
  const cb = state.commandBuffer;
  const snap = state.commandCancelSnapshot ?? state.commandPreSnapshot;
  recordCommandCancel();
  clearLiveEditPreview();
  if (snap) {
    const patch: Partial<AppState> = isEditObjectSnapshot(snap)
      ? applyEditObjectSnapshot(state, snap)
      : applySnapshot(state, snap);
    // Full snapshots are pushed on command-enter. Edit-object snapshots are
    // local command drafts and do not touch the undo stack until confirm.
    if (!isEditObjectSnapshot(snap)) {
      undoManager.undo(captureSnapshot(state));
    }
    // Restore pre-operation selection (snapshots don't capture editMode selection)
    if (cb?.preEditSelection && state.editMode) {
      patch.editMode = {
        ...state.editMode,
        selectedVertices: cb.preEditSelection,
        selectedEdges: cb.preEditEdges ?? new Set<number>(),
        selectedFaces: cb.preEditFaces ?? new Set<number>(),
        lastActiveVertex: cb.preEditActiveVertex,
        lastActiveEdge: cb.preEditActiveEdge,
        lastActiveFace: cb.preEditActiveFace,
      };
    }
    // Rebuild curveEditVertices from restored curveData
    if (state.editMode?.isCurve && patch.sceneObjects) {
      const restoredObj = patch.sceneObjects.get(state.editMode!.objectId);
      if (restoredObj?.curveData) {
        patch.editMode = {
          ...(patch.editMode ?? state.editMode!),
          curveEditVertices: buildCurveEditVertices(restoredObj.curveData),
        };
      }
    }
    setLastSnapResult(null);
    destroyIKSession();
    destroyWorkerSession();
    releaseLiveVertsScratch();
    set({
      ...patch,
      activeEditSession: null,
      commandBuffer: null,
      commandPreSnapshot: null,
      commandCancelSnapshot: null,
      ikDragState: null,
      canUndo: undoManager.canUndo,
      canRedo: undoManager.canRedo,
    });
  } else {
    setLastSnapResult(null);
    destroyIKSession();
    destroyWorkerSession();
    releaseLiveVertsScratch();
    set({
      activeEditSession: null,
      commandBuffer: null,
      commandPreSnapshot: null,
      commandCancelSnapshot: null,
      ikDragState: null,
    });
  }
}

export function adjustLastOperation(
  set: Set_,
  get: Get_,
  newParams: Record<string, number | boolean | string>,
): void {
  const lastOp = get().lastOperation;
  if (!lastOp) return;
  const strategy = REPLAY_STRATEGIES[lastOp.replayKind];
  if (!strategy) return;
  strategy.replay(set, get, lastOp, newParams);
}

export function clearLastOperation(set: Set_): void {
  set({ lastOperation: null });
}
